import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Search, X } from "lucide-react";
import { Input } from "@/shared/components/ui/input";
import { Spinner } from "@/shared/components/ui/spinner";
import { useDebouncedSearch } from "@/shared/hooks/useDebouncedSearch";
import { getApiErrorMessage } from "@/shared/lib/api-error";
import { tagService } from "@/features/tags/services/tags.service";
import type { TagResponseDTO } from "@/features/tags";
import { TagBadge } from "@/features/tags";

interface TagMultiSelectFieldProps {
  value: number[];
  onChange: (ids: number[]) => void;
  disabled?: boolean;
  inputId?: string;
}

const DEBOUNCE_MS = 300;
const SEARCH_PAGE_SIZE = 8;

// Consome tagService (USER) — tagAdminService retornaria 403 para USER.
export function TagMultiSelectField({
  value,
  onChange,
  disabled = false,
  inputId,
}: TagMultiSelectFieldProps) {
  const [tagCache, setTagCache] = useState<Map<number, TagResponseDTO>>(
    () => new Map(),
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [results, setResults] = useState<TagResponseDTO[]>([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const lookedUpIdsRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    const missing = value.filter(
      (id) => !tagCache.has(id) && !lookedUpIdsRef.current.has(id),
    );
    if (missing.length === 0) return;
    missing.forEach((id) => lookedUpIdsRef.current.add(id));
    let cancelled = false;
    Promise.all(
      missing.map((id) => tagService.getById(id).catch(() => null)),
    ).then((tags) => {
      if (cancelled) return;
      const resolved = tags.filter((t): t is TagResponseDTO => t !== null);
      if (resolved.length === 0) return;
      setTagCache((prev) => {
        const next = new Map(prev);
        resolved.forEach((tag) => next.set(tag.id, tag));
        return next;
      });
    });
    return () => {
      cancelled = true;
    };
    // tagCache é lido via closure ao decidir quais ids buscar; o efeito
    // não deve re-disparar quando o cache cresce — refetch evitado pelo
    // lookedUpIdsRef.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const fetchTags = useCallback(async (term: string, signal: AbortSignal) => {
    const response = await tagService.search(
      {
        name: term,
        size: SEARCH_PAGE_SIZE,
        sortBy: "name",
        sortDirection: "ASC",
      },
      { signal },
    );
    setTagCache((prev) => {
      const next = new Map(prev);
      response.content.forEach((tag) => next.set(tag.id, tag));
      return next;
    });
    return response.content;
  }, []);

  useDebouncedSearch({
    term: searchTerm,
    fetcher: fetchTags,
    setItems: setResults,
    setLoading: setSearching,
    onError: (e) => toast.error(getApiErrorMessage(e, "Erro ao buscar tags")),
    debounceMs: DEBOUNCE_MS,
  });

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const handleAdd = useCallback(
    (tag: TagResponseDTO) => {
      if (value.includes(tag.id)) return;
      onChange([...value, tag.id]);
      setSearchTerm("");
      setResults([]);
    },
    [value, onChange],
  );

  const handleRemove = useCallback(
    (id: number) => {
      onChange(value.filter((current) => current !== id));
    },
    [value, onChange],
  );

  const visibleResults = results.filter((tag) => !value.includes(tag.id));

  return (
    <div ref={containerRef} className="relative">
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {value.map((id) => {
            const tag = tagCache.get(id);
            return (
              <span key={id} className="inline-flex items-center gap-1.5">
                {tag ? (
                  <TagBadge tag={tag} size="sm" />
                ) : (
                  <span className="text-xs text-muted-foreground">#{id}</span>
                )}
                <button
                  type="button"
                  onClick={() => handleRemove(id)}
                  disabled={disabled}
                  aria-label={`Remover tag ${tag?.name ?? `#${id}`}`}
                  className="rounded-full hover:bg-accent p-0.5"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            );
          })}
        </div>
      )}

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
        <Input
          id={inputId}
          value={searchTerm}
          placeholder="Buscar tag pelo nome…"
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          disabled={disabled}
          className="pl-9"
        />
      </div>

      {open && searchTerm.trim().length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-popover border rounded-md shadow-md max-h-60 overflow-y-auto z-50">
          {searching ? (
            <div className="flex items-center justify-center gap-2 p-3 text-sm text-muted-foreground">
              <Spinner className="w-4 h-4" />
              Buscando…
            </div>
          ) : visibleResults.length === 0 ? (
            <div className="p-3 text-center text-sm text-muted-foreground">
              Nenhuma tag encontrada
            </div>
          ) : (
            visibleResults.map((tag) => (
              <button
                key={tag.id}
                type="button"
                onClick={() => handleAdd(tag)}
                className="w-full text-left px-3 py-2 hover:bg-accent text-sm"
              >
                <TagBadge tag={tag} size="sm" />
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
